import type { ChatHeaderProps } from "@qw/chat";
import { Column, cn, Separator } from "@qw/ui";
import { useEffect, useMemo } from "react";
import { useChatDock } from "@/chat/chat-dock";
import { ChatSession } from "@/chat/chat-session";
import { useChatScope } from "@/chat/use-chat-scope";
import { ChatPosition } from "@/providers/page-provider";
import { usePage } from "@/providers/use-page";

interface ChatColumnProps {
  layout?: "flex" | "panel";
  mountOnly?: boolean;
}

export const ChatColumn = ({ layout = "flex", mountOnly = false }: ChatColumnProps = {}) => {
  const { chatPanel, chatPosition, isChatOpen, setChatMounted } = usePage();
  const { toolFilterStorageKey } = useChatScope();
  const dock = useChatDock();
  const shouldRender = isChatOpen && !mountOnly;

  const header = useMemo<ChatHeaderProps>(
    () => ({
      // The session picker, tool filter, run history and position all moved to
      // the Alby menu in the nav (B-2048). The sidebar sits beside the nav and
      // needs nothing of its own; the dock overlays it and brings its own.
      actions: dock.headerActions,
      className: dock.headerClassName,
      hideSessionLabel: true,
      sticky: true,
    }),
    // The two stable halves, not `dock` itself: its width changes every frame of
    // a resize, and the header has no business rebuilding for that.
    [dock.headerActions, dock.headerClassName]
  );

  useEffect(() => {
    setChatMounted(true);
    return () => {
      setChatMounted(false);
    };
  }, [setChatMounted]);

  if (!shouldRender) {
    return null;
  }

  return (
    <>
      {chatPosition === ChatPosition.side && layout === "flex" && <Separator orientation="vertical" />}
      <Column
        className={cn(
          "bg-background min-h-0 min-w-0 overflow-x-hidden",
          layout === "flex" && chatPosition === ChatPosition.side && "flex-1",
          dock.className
        )}
        style={dock.style}
      >
        {dock.resizeHandle}
        {chatPanel ?? <ChatSession header={header} toolFilterStorageKey={toolFilterStorageKey} />}
      </Column>
    </>
  );
};
